import React, { useEffect } from 'react';
import { calcIsEmptyOverviewCard } from '../../utils';

/**
 * 自定义钩子：用于检测并设置是否存在空指标数据
 *
 * 主要功能：
 * 1. 在加载完成后检查指标数据
 * 2. 处理空指标数据集的情况
 * 3. 排除特定数据源的空指标检测
 * 4. 通过回调函数返回检测结果
 *
 * @param props - 钩子参数对象
 * @param props.executable - 是否可执行hooks内的逻辑（可选，默认false）
 * @param props.metrics - 指标数据数组（可能为null）
 * @param props.excludes - 需要排除的数据源名称数组（可选）
 * @param props.setHasEmptyMetric - 设置空指标状态的回调函数
 */
export default function useCalcHasEmptyMetric(props: {
  executable?: boolean;
  metrics?: any[] | null;
  excludes?: string[] | null;
  setHasEmptyMetric: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  // 从props中解构所需参数并设置默认值
  const { executable = false, metrics, excludes, setHasEmptyMetric } = props;

  // 使用useEffect在依赖项变化时执行检测逻辑
  useEffect(() => {
    // 还不能执行hooks内的逻辑，则跳过检测
    if (!executable) return;

    // 情况1：处理空指标数据集
    // 当metrics不是数组或为空数组时，直接判定存在空指标
    if (!Array.isArray(metrics) || metrics.length === 0) {
      setHasEmptyMetric(true);
      return;
    }

    // 情况2：检查是否存在非排除的空指标
    // 使用Array.some()遍历指标数组，寻找符合条件的空指标
    const hasEmptyMetric = metrics.some((metric) => {
      // 条件1：当前指标的数据源不在排除列表中
      // 条件2：通过工具函数判断指标是否为空
      return !(Array.isArray(excludes) && excludes.includes(metric?.data_source)) && calcIsEmptyOverviewCard(metric);
    });

    // 将最终检测结果通过回调函数返回
    setHasEmptyMetric(hasEmptyMetric);
  }, [executable, metrics, excludes]); // 依赖项：当这些值变化时重新执行检测
}
